import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Slider from 'react-slick';
import { Link } from 'react-router-dom';
import styles from '../../styles/HomeReviews.module.scss';
import '../../styles/slick.scss';
import '../../styles/slick-theme.scss';
import { getPublicReviews } from '../../Store/reviews/reviewsSlice';
import CarouselNav from '../common/CarouselNav';
import { avatarUrl } from '../../utils/imageUrl';

const AVATAR_COLORS = ['#6fcf4b', '#8b57c6', '#2f80c8', '#f2994a', '#56ccf2'];

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const getInitial = (name) => {
  const trimmed = String(name || '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

const getAvatarColor = (name) => {
  const code = String(name || '').split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const Stars = ({ rating }) => (
  <div className={styles.stars} aria-label={`Оцінка ${rating} з 5`}>
    {Array.from({ length: 5 }, (_, index) => (
      <span key={index} className={index < rating ? styles.starActive : styles.star}>
        ★
      </span>
    ))}
  </div>
);

const ReviewAvatar = ({ name, avatar }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const src = avatarUrl(avatar);
  const showImage = Boolean(src) && !imageFailed;

  return (
    <div
      className={styles.avatar}
      style={!showImage ? { backgroundColor: getAvatarColor(name) } : undefined}
      aria-hidden="true"
    >
      {showImage ? (
        <img src={src} alt="" onError={() => setImageFailed(true)} />
      ) : (
        getInitial(name)
      )}
    </div>
  );
};

const ReviewCard = ({ review }) => {
  const userName = review.User?.name || 'Покупець';

  return (
    <article className={styles.card}>
      <div className={styles.cardHeader}>
        <ReviewAvatar name={userName} avatar={review.User?.avatar} />
        <div className={styles.meta}>
          <div className={styles.nameRow}>
            <h3 className={styles.name}>{userName}</h3>
            <time className={styles.date} dateTime={review.createdAt}>
              {formatDate(review.createdAt)}
            </time>
          </div>
          <Stars rating={review.rating} />
        </div>
      </div>
      <p className={styles.text}>{review.text}</p>
      {review.productId && (
        <Link to={`/products/${review.productId}`} className={styles.productLink}>
          Переглянути товар
        </Link>
      )}
    </article>
  );
};

const HomeReviews = () => {
  const dispatch = useDispatch();
  const sliderRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const { homeReviews, homeReviewsLoading } = useSelector((state) => state.reviews);

  useEffect(() => {
    dispatch(getPublicReviews());
  }, [dispatch]);

  const count = homeReviews.length;
  const slidesToShow = Math.min(2, count);
  const useCarousel = count > slidesToShow;
  const pageCount = useCarousel ? count - slidesToShow + 1 : 0;

  const settings = useMemo(() => ({
    dots: false,
    infinite: useCarousel,
    speed: 500,
    slidesToShow: slidesToShow || 1,
    slidesToScroll: 1,
    autoplay: count > 1,
    autoplaySpeed: 5000,
    arrows: false,
    pauseOnHover: true,
    beforeChange: (_current, next) => setSlideIndex(next),
  }), [count, useCarousel, slidesToShow]);

  if (homeReviewsLoading && count === 0) {
    return null;
  }

  if (count === 0) {
    return null;
  }

  const items = homeReviews.map((review) => (
    <div key={review.id} className={styles.slide}>
      <ReviewCard review={review} />
    </div>
  ));

  return (
    <section className={`${styles.section}${useCarousel ? ` ${styles.sectionCarousel}` : ''} homeFullBleed`}>
      <h2 className={styles.title}>Відгуки</h2>
      {useCarousel ? (
        <>
          <div className={styles.sliderWrap}>
            <Slider ref={sliderRef} {...settings}>{items}</Slider>
          </div>
          <CarouselNav
            total={pageCount}
            current={Math.min(slideIndex, pageCount - 1)}
            onPrev={() => sliderRef.current?.slickPrev()}
            onNext={() => sliderRef.current?.slickNext()}
            onDotClick={(index) => sliderRef.current?.slickGoTo(index)}
          />
        </>
      ) : (
        <div className={styles.grid}>
          {homeReviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      )}
    </section>
  );
};

export default HomeReviews;
